import type { Product, ProductDetail, ProductImportResult } from '@/domain/entities/product.entity'
import { addonGroupMapper } from './addon-group.mapper'
import { modifierMapper } from './modifier.mapper'
import type { ImportCountDto, ImportResultDto, ProductDto } from './product.dto'

export const productMapper = {
  toProduct(dto: ProductDto): Product {
    return {
      id: dto.id,
      sku: dto.sku,
      name: dto.name,
      category: dto.category ? { id: dto.category.id, name: dto.category.name } : null,
      price1: dto.price1 ?? 0,
      price2: dto.price2 ?? 0,
      price3: dto.price3 ?? 0,
      manualHpp: dto.manual_hpp ?? null,
      imagePath: dto.image_path ?? null,
      stockActive: Boolean(dto.stock_active),
      stock: dto.stock ?? 0,
      variantGroupName: dto.variant_group_name ?? null,
      isPackage: Boolean(dto.is_package),
      sourceId: dto.source_id ?? null,
      active: dto.active ?? true,
    }
  },

  toProductDetail(dto: ProductDto): ProductDetail {
    return {
      ...productMapper.toProduct(dto),
      variants: (dto.variants ?? []).map(modifierMapper.toModifier),
      addons: (dto.addons ?? []).map(modifierMapper.toModifier),
      addonGroups: (dto.addon_groups ?? []).map(addonGroupMapper.toAddonGroup),
      packageItems: (dto.package_items ?? []).map((pi) => ({
        productId: pi.product_id,
        qty: pi.qty,
        ord: pi.ord,
      })),
      packageGroups: (dto.package_groups ?? []).map((g) => ({
        id: g.id ?? '',
        name: g.name,
        ord: g.ord ?? 0,
        items: (g.items ?? []).map((it) => ({
          id: it.id ?? '',
          productId: it.product_id ?? it.product?.id ?? '',
          qty: it.qty,
          ord: it.ord ?? 0,
          product: it.product ? productMapper.toProduct(it.product) : null,
        })),
      })),
    }
  },

  toImportResult(dto: ImportResultDto): ProductImportResult {
    const count = (c?: ImportCountDto | null) => ({
      created: c?.created ?? 0,
      updated: c?.updated ?? 0,
    })
    return {
      products: count(dto.products),
      variants: count(dto.variants),
      addons: count(dto.addons),
      addonGroups: count(dto.addon_groups),
      links: {
        productVariants: dto.links?.product_variants ?? 0,
        productAddons: dto.links?.product_addons ?? 0,
        addonGroupAddons: dto.links?.addon_group_addons ?? 0,
        productAddonGroups: dto.links?.product_addon_groups ?? 0,
      },
      failed: (dto.failed ?? []).map((f) => ({
        sheet: f.sheet,
        row: f.row,
        key: f.key,
        error: f.error,
      })),
    }
  },
}
